import {cn} from '@tempmee/core'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {
  MenuColorProvider,
  MenuDensityProvider,
  MenuHeader,
  MenuSizeProvider,
  SidebarCollapsedProvider,
  useMenuColor,
  useSidebarCollapsed,
} from '../menu'
import {SidebarVariantsFactory} from './Variants'

/** Width of the mini (collapsed) rail — just wide enough for a centered icon. */
const COLLAPSED_WIDTH = 72

// ---------------------------------------------------------------------------
// Sidebar (root) — persistent left-nav rail shell.
//
// In v3 the rail is composed from the shared menu primitives:
//   <Sidebar>
//     <Sidebar.Header title=… avatar=… />     {/* = MenuHeader */}
//     <Sidebar.Content>
//       <MenuItem icon=… selected>Dashboard</MenuItem>
//       …
//     </Sidebar.Content>
//   </Sidebar>
//
// `Sidebar.Item` is removed — use `MenuItem` from `@tempmee/react` instead.
// ---------------------------------------------------------------------------

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode
  /**
   * `light` — white rail with dark rows; `dark` — deep-teal `primary-contrast`
   * brand rail with white rows. Propagates via context (like `dense`), so every
   * descendant `MenuItem` / `CollapsibleMenuItem` / `Sidebar.Header` inverts with
   * the rail. Pair with `<TopNav color="dark">` for a single themed shell.
   * @default 'light'
   */
  color?: 'light' | 'dark'
  /**
   * Row height for the rail's **top-level** rows: `md` 36px (the MUI-aligned
   * default) or `lg` 48px (Figma's roomier nav rail). Rows nested inside a
   * `CollapsibleMenuItem` stay 36px either way, so `lg` yields the design's
   * 48px-parent / 36px-child pairing. `dense` still wins (32px throughout).
   * @default 'md'
   */
  size?: 'md' | 'lg'
  /** Width of the rail. Accepts any CSS value or pixel number. @default 300 */
  width?: number | string
  /**
   * Compact "tight" rail — propagates 32px `dense` rows to every descendant
   * `MenuItem` / `CollapsibleMenuItem` (via context), so you set density once
   * instead of per item. Individual items can still override with their own
   * `dense`. Pair with a narrower `width` (e.g. 240) for a MUI-style tight rail.
   * @default false
   */
  dense?: boolean
  /**
   * Collapse the rail to a mini, icons-only strip (~72px). Propagates via context
   * (like `dense`): `MenuItem`s render icon-only with a label tooltip, and
   * `CollapsibleMenuItem`s swap their inline panel for a flyout popover. The app
   * owns the toggle (e.g. a header hamburger) and passes the boolean. @default false
   * @experimental
   */
  collapsed?: boolean
}

const SidebarRoot = forwardRef<HTMLElement, SidebarProps>(
  (
    {
      className,
      children,
      color = 'light',
      size = 'md',
      width = 300,
      dense = false,
      collapsed = false,
      style,
      ...props
    },
    ref,
  ) => (
    <aside
      ref={ref}
      className={cn(SidebarVariantsFactory({color, collapsed}), className)}
      style={{
        width: collapsed
          ? `${COLLAPSED_WIDTH}px`
          : typeof width === 'number'
            ? `${width}px`
            : width,
        ...style,
      }}
      {...props}
    >
      <MenuColorProvider value={color}>
        <MenuSizeProvider value={size}>
          <SidebarCollapsedProvider value={collapsed}>
            <MenuDensityProvider value={dense}>{children}</MenuDensityProvider>
          </SidebarCollapsedProvider>
        </MenuSizeProvider>
      </MenuColorProvider>
    </aside>
  ),
)
SidebarRoot.displayName = 'Sidebar'

export interface SidebarContentProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode
}

/**
 * Scrollable nav region holding the rail's `MenuItem`s. Owns the rail's overflow,
 * so a long nav scrolls inside the rail while `Sidebar.Header` stays put. It wears
 * the DS scrollbar skin (`ds-scroll-area`), in its dark-surface flavour on a
 * `color="dark"` rail.
 *
 * It also bleeds out over the rail's gutter (`-mx-4 px-4`) and re-pads itself, so
 * the scrollbar lands flush against the rail's edge instead of floating 16px
 * inside it — the rows keep exactly the same inset either way.
 */
const SidebarContent = forwardRef<HTMLElement, SidebarContentProps>(
  ({className, children, ...props}, ref) => {
    const collapsed = useSidebarCollapsed()
    const dark = useMenuColor() === 'dark'
    return (
      <nav
        ref={ref}
        className={cn(
          'ds-scroll-area flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pt-6 pb-10',
          // Cancel the rail's gutter, then restore it as padding inside the
          // scroll container (see the note above).
          collapsed ? '-mx-2 px-2' : '-mx-4 px-4',
          dark && 'ds-scroll-area-dark',
          className,
        )}
        {...props}
      >
        {children}
      </nav>
    )
  },
)
SidebarContent.displayName = 'Sidebar.Content'

export const Sidebar = Object.assign(SidebarRoot, {
  /** Profile header (alias of `MenuHeader`). */
  Header: MenuHeader,
  Content: SidebarContent,
})
